import { useCallback, useEffect, useState } from 'react';
import type { Eu } from '../../acesso';
import { ErroDeArea } from '../../design/Estados';
import { Icone } from '../../design/Icone';
import { CartaoDeGrafico } from '../../design/graficos/CartaoDeGrafico';
import { MODELOS_PARTES, fmt, partes } from '../../design/graficos/modelos';
import { hojeBrasilia, lerHojeNoInovapay } from '../../paineis';
import type { HojeNoInovapay } from '../../paineis';
import { lerSemana } from '../../premiacao';
import type { ChequeDaSemana } from '../../premiacao';
import { Atencao, Operacao } from '../paineis/Home';
import { Kpi, nomeCurto } from '../paineis/pecasDaVisao';
import { BarraDoPrograma, filtroDoPrograma, semanaDoCheque } from './Programa';
import '../central/central.css';
import './inovapay.css';

/**
 * PAINEL DO INOVAPAY — a semana dos programas (vitamina, pré-vencido, paradão), com a MESMA conta
 * do cheque de sábado: quanto cada um tem a receber, o que bateu com o HOS e o que não bateu
 * (lançado e não vendido, vendido e não lançado, dia errado, devolvido, cancelado). E o que é de
 * hoje: Giro, validades e o que pede decisão. Nenhum número da loja (faturamento, custo, margem):
 * esta é a primeira tela de quem administra o InovaPay.
 */

const PROGRAMAS = [
  { id: 'vitamina' as const, nome: 'Vitamina', cor: '#1a7f55', link: '#/vitaminas' },
  { id: 'pre_vencido' as const, nome: 'Pré-vencido', cor: '#cb1b2b', link: '#/pre-vencidos' },
  { id: 'paradao' as const, nome: 'Paradão', cor: '#b7791f', link: '#/paradao' },
];
const diaCurto = (d: string) => `${d.slice(8, 10)}/${d.slice(5, 7)}`;
const primeiroNome = (s: string) => {
  const p = s.trim().split(/\s+/)[0] ?? s;
  return p.charAt(0).toUpperCase() + p.slice(1).toLowerCase();
};
const soma = (xs: readonly number[]) => xs.reduce((a, b) => a + b, 0);

export function PainelInovaPay({ eu, parametros }: { eu: Eu | null; parametros: URLSearchParams }) {
  const hoje = hojeBrasilia();
  // Os cinco botões dos programas (dono, 05/10); o endereço antigo (?semana=) abre aquela semana.
  const pedida = parametros.get('semana');
  const f =
    !parametros.get('p') && pedida && /^\d{4}-\d{2}-\d{2}$/.test(pedida)
      ? { filtro: 'livre' as const, periodo: semanaDoCheque(pedida), pessoa: null }
      : filtroDoPrograma(parametros);
  const semana = f.periodo;
  const ehAtual = f.filtro === 'semana';
  const [c, setC] = useState<ChequeDaSemana | null>(null);
  const [h, setH] = useState<HojeNoInovapay | null>(null);
  const [erro, setErro] = useState(false);
  const [erroDeHoje, setErroDeHoje] = useState(false);

  const carregarHoje = useCallback(() => {
    setErroDeHoje(false);
    void lerHojeNoInovapay().then((r) => (r.ok ? setH(r.dados) : setErroDeHoje(true)));
  }, []);
  const carregar = useCallback(() => {
    setErro(false);
    setC(null);
    void lerSemana(semana.de, semana.ate).then((r) => (r.ok ? setC(r.dados) : setErro(true)));
    carregarHoje();
  }, [semana.de, semana.ate, carregarHoje]);
  useEffect(carregar, [carregar]);

  // Os links levam a MESMA semana: a conferência sem `semana` abriria a última fechada, e a
  // visão do programa, o mês.
  const daSemana = `semana=${semana.de}`;
  const linkConferencia = `#/conferencia?${daSemana}`;
  const linkPremiacao = `#/premiacao?${daSemana}`;
  const periodoDoPrograma =
    f.filtro === 'livre'
      ? `p=livre&de=${semana.de}&ate=${semana.ate > hoje ? hoje : semana.ate}`
      : `p=${f.filtro}`;
  const pessoas = c?.pessoas ?? [];
  const conta = (k: keyof ChequeDaSemana['pessoas'][number]['linhas']) =>
    soma(pessoas.map((p) => p.linhas[k].length));
  const bateram = pessoas.filter((p) => p.metaBatida).length;
  const comMeta = pessoas.filter((p) => p.meta > 0).length;
  const porPrograma = PROGRAMAS.map((p) => ({
    ...p,
    devido: c ? c.totais.porTipo[p.id].devido : 0,
    itens: soma(
      pessoas.map((x) => soma(x.linhas.bate.filter((l) => l.programa === p.id).map((l) => l.qtd))),
    ),
  }));
  const top = [...pessoas].sort((a, b) => b.aReceber - a.aReceber).filter((p) => p.aReceber > 0);
  const nome = eu?.nomeExibicao ?? eu?.nome ?? '';

  return (
    <section className="cv">
      <div className="cv-corpo vg">
        <header className="vg-topo">
          <div className="vg-titulo">
            <div className="vg-saudacao">
              {nome ? `Olá, ${primeiroNome(nome)}` : 'InovaPay'} · vitamina, pré-vencido, paradão e
              Giro
            </div>
            <h1>Painel do InovaPay</h1>
            <div className="vg-contexto">
              De {diaCurto(semana.de)} a {diaCurto(semana.ate)}
              {ehAtual
                ? ' · a semana fecha sexta às 23:59 · a conta é a mesma do cheque de sábado'
                : ' · a conta é a mesma do cheque de sábado'}
            </div>
          </div>
          <div className="vg-acoes">
            <a className="vg-botao" href={`#/tudo-por-balconista?${periodoDoPrograma}`}>
              <Icone nome="users" tamanho={15} traco={1.6} />
              Tudo por balconista
            </a>
            <a className="vg-botao vg-botao-escuro" href={linkConferencia}>
              <Icone nome="check" tamanho={15} traco={1.6} />
              Conferência da semana
            </a>
          </div>
        </header>
        <BarraDoPrograma f={f} rota="inovapay" maxDias={62} />

        {erro ? (
          <ErroDeArea oQue="a semana do InovaPay" aoTentar={carregar} />
        ) : (
          <>
            <div className="vg-kpis">
              <Kpi
                rotulo="A receber no período"
                icone="money"
                valor={c ? fmt(c.totais.aReceber, 'reais') : null}
                variacao={null}
                sub={c ? `${fmt(top.length, 'inteiro')} pessoa(s) com prêmio` : null}
                link={linkPremiacao}
                destaque
              />
              {porPrograma.map((p) => (
                <Kpi
                  key={p.id}
                  rotulo={p.nome}
                  icone={p.id === 'vitamina' ? 'pill' : p.id === 'pre_vencido' ? 'clock' : 'box'}
                  valor={c ? fmt(p.devido, 'reais') : null}
                  variacao={null}
                  sub={c ? `${fmt(p.itens, 'inteiro')} itens conferidos` : null}
                  link={`${p.link}?${periodoDoPrograma}`}
                />
              ))}
              <Kpi
                rotulo="Meta da semana"
                icone="trophy"
                valor={c ? (comMeta ? `${String(bateram)} de ${String(comMeta)}` : '—') : null}
                variacao={null}
                sub={c ? 'bateram a meta de itens' : null}
                barra={comMeta ? (bateram / comMeta) * 100 : null}
                link="#/ranking"
              />
              <Kpi
                rotulo="Saiu da premiação"
                icone="arrow-l"
                valor={c ? fmt(c.totais.devolucao + c.totais.cancelado, 'reais') : null}
                variacao={null}
                sub={
                  c
                    ? `${fmt(c.totais.devolucao, 'reais')} devolução · ${fmt(c.totais.cancelado, 'reais')} cancelado`
                    : null
                }
                link={linkConferencia}
              />
            </div>

            <div className="vg-bloco ip-conferencia">
              <div className="vg-bloco-topo">
                <div>
                  <h2>Conferência da semana</h2>
                  <p>
                    O lançado contra as vendas do HOS, linha a linha — a mesma leitura do cheque
                  </p>
                </div>
                <a href={linkConferencia} className="vg-bloco-link">
                  Abrir a conferência
                  <Icone nome="chev-r" tamanho={13} traco={1.8} />
                </a>
              </div>
              <div className="ip-contas">
                <Conta
                  rotulo="Bateu com o HOS"
                  n={c ? conta('bate') : null}
                  link={linkConferencia}
                  tom="ok"
                />
                <Conta
                  rotulo="Lançou e não vendeu"
                  n={c ? conta('naoVendeu') : null}
                  link={linkConferencia}
                  tom="ruim"
                />
                <Conta
                  rotulo="Vendeu e não lançou"
                  n={c ? conta('naoLancou') : null}
                  link={linkConferencia}
                  tom="atencao"
                />
                <Conta
                  rotulo="Lançou em dia errado"
                  n={c ? conta('diaErrado') : null}
                  link={linkConferencia}
                  tom="atencao"
                />
                <Conta
                  rotulo="Devolvidos"
                  n={c ? conta('devolvidos') : null}
                  link={linkConferencia}
                  tom="neutro"
                />
                <Conta
                  rotulo="Cancelados"
                  n={c ? conta('canceladas') : null}
                  link={linkConferencia}
                  tom="neutro"
                />
              </div>
              {c && c.avisos.diasSemRelatorio.length > 0 && (
                <p className="vg-nota">
                  Sem vendas do HOS em {c.avisos.diasSemRelatorio.map(diaCurto).join(', ')}: os
                  lançamentos desses dias ficam aguardando, não viram "não vendeu".
                </p>
              )}
            </div>

            {c && top.length > 0 && (
              <div className="vg-linha cv-linha-2">
                <CartaoDeGrafico
                  id="ip-receber"
                  titulo="A receber por balconista"
                  numero={
                    top[0] ? `${nomeCurto(top[0].nome)} · ${fmt(top[0].aReceber, 'reais')}` : '—'
                  }
                  conclusao={`${String(top.length)} pessoa(s) com prêmio no período; ${String(pessoas.length - top.length)} sem prêmio (sem lançamento conferido ou meta não batida).`}
                  modelos={MODELOS_PARTES}
                  padrao="barras"
                  desenhar={(m) =>
                    partes(m, {
                      itens: top
                        .slice(0, 12)
                        .map((p) => ({ nome: nomeCurto(p.nome), valor: p.aReceber })),
                      formato: 'reais',
                    })
                  }
                  fonte="Fonte: cheque da semana (lançamentos × vendas do HOS)"
                  altura={280}
                />
                <CartaoDeGrafico
                  id="ip-programa"
                  titulo="Prêmio por programa"
                  numero={fmt(soma(porPrograma.map((p) => p.devido)), 'reais')}
                  conclusao={(() => {
                    const lider = [...porPrograma].sort((a, b) => b.devido - a.devido)[0];
                    return lider && lider.devido ? (
                      <>
                        <strong>{lider.nome}</strong> é{' '}
                        {fmt(
                          (lider.devido / Math.max(1, soma(porPrograma.map((p) => p.devido)))) *
                            100,
                          'pct',
                        )}{' '}
                        do prêmio conferido.
                      </>
                    ) : (
                      'Nenhum prêmio conferido.'
                    );
                  })()}
                  modelos={MODELOS_PARTES}
                  padrao="rosca"
                  desenhar={(m) =>
                    partes(m, {
                      itens: porPrograma
                        .filter((p) => p.devido > 0)
                        .map((p) => ({ nome: p.nome, valor: p.devido, cor: p.cor })),
                      formato: 'reais',
                    })
                  }
                  fonte="Fonte: cheque da semana"
                  altura={280}
                />
              </div>
            )}

            {erroDeHoje && !h ? (
              <ErroDeArea oQue="a operação de hoje e o que pede atenção" aoTentar={carregarHoje} />
            ) : (
              <div className="vg-linha vg-linha-principal">
                {h ? (
                  <Operacao modulos={h.operacao} giro={null} hoje={h.dia} />
                ) : (
                  <div className="hm-cartao vg-osso" />
                )}
                {h && c ? (
                  <Atencao
                    itens={h.atencao}
                    premiacao={{
                      disponivel: true,
                      valor: c.totais.aReceber,
                      detalhe: `${diaCurto(semana.de)}–${diaCurto(semana.ate)}`,
                      variacao: null,
                      sentido: 'neutro',
                      serie: null,
                      fonte: 'cheque da semana',
                      link: linkPremiacao,
                    }}
                  />
                ) : (
                  <div className="hm-cartao vg-osso" />
                )}
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}

function Conta({
  rotulo,
  n,
  tom,
  link,
}: {
  rotulo: string;
  n: number | null;
  tom: 'ok' | 'ruim' | 'atencao' | 'neutro';
  link: string;
}) {
  return (
    <a className={`ip-conta ip-conta-${tom}`} href={link}>
      <strong>{n === null ? '…' : fmt(n, 'inteiro')}</strong>
      <span>{rotulo}</span>
    </a>
  );
}
